import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { debtorService } from '../services/debtorService';
import { notificationService } from '../services/notificationService';
import { exportService } from '../services/exportService';
import { useNotification } from '../hooks/useNotification';
import { useAuth } from '../hooks/useAuth';
import { formatCurrency, formatDateTime } from '../utils/formatters';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';
import AddDebtModal from '../components/transaction/AddDebtModal';
import RecordPaymentModal from '../components/transaction/RecordPaymentModal';
import EditDebtorModal from '../components/debtor/EditDebtorModal';
import DeleteDebtorModal from '../components/debtor/DeleteDebtorModal';
import ThresholdAlertModal from '../components/debtor/ThresholdAlertModal';

const DebtorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [debtor, setDebtor] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showAddDebt, setShowAddDebt] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showThresholdAlert, setShowThresholdAlert] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    loadDebtorDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDebtorDetails = async () => {
    try {
      const response = await debtorService.getById(id);
      setDebtor(response.data.debtor);
      setTransactions(response.data.transactions || []);
      setAlert(response.data.alert);

      // Show threshold alert if exists
      if (response.data.alert?.thresholdExceeded) {
        setShowThresholdAlert(true);
      }
    } catch (error) {
      console.error('Error loading debtor:', error);
      showError(error.message || 'Failed to load debtor details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSuccess = () => {
    showSuccess('Debtor deleted successfully');
    navigate('/dashboard');
  };

  const handleSendReminder = async () => {
    if (debtor.totalOutstanding <= 0) {
      showError('No outstanding balance for this debtor');
      return;
    }

    if (window.confirm(`Send payment reminder to ${debtor.name}?`)) {
      setSendingReminder(true);
      try {
        await notificationService.sendReminder(id);
        showSuccess('Payment reminder sent successfully!');
      } catch (error) {
        showError(error.message || 'Failed to send reminder');
      } finally {
        setSendingReminder(false);
      }
    }
  };

  const handleExportTransactionsExcel = async () => {
    try {
      await exportService.exportTransactionsExcel(id);
      showSuccess('Excel file downloaded successfully!');
      setShowExportMenu(false);
    } catch (error) {
      showError(error.message || 'Failed to export');
    }
  };

  const handleExportTransactionsCSV = async () => {
    try {
      await exportService.exportTransactionsCSV(id);
      showSuccess('CSV file downloaded successfully!');
      setShowExportMenu(false);
    } catch (error) {
      showError(error.message || 'Failed to export');
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (!debtor) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back
              </button>
              {user?.logo?.url && (
                <img
                  src={`${process.env.REACT_APP_API_URL}${user.logo.url}`}
                  alt={user?.businessName}
                  className="w-10 h-10 object-contain rounded"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{debtor.name}</h1>
                <p className="text-sm text-gray-600">{debtor.mobile}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowEdit(true)}>
                Edit
              </Button>
              <Button variant="danger" size="sm" onClick={() => setShowDelete(true)}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-2">Total Outstanding</p>
            <p className="text-3xl font-bold text-red-600">
              {formatCurrency(debtor.totalOutstanding)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-2">Total Paid</p>
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(debtor.totalPaid)}
            </p>
          </div>
        </div>

        {/* Notes */}
        {debtor.notes && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> {debtor.notes}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <Button onClick={() => setShowAddDebt(true)}>
            + Add Debt
          </Button>
          <Button variant="success" onClick={() => setShowRecordPayment(true)}>
            Record Payment
          </Button>
          {debtor.totalOutstanding > 0 && (
            <Button
              onClick={handleSendReminder}
              loading={sendingReminder}
              variant="warning"
            >
              Send Reminder
            </Button>
          )}
          <div className="relative">
            <Button onClick={() => setShowExportMenu(!showExportMenu)} variant="outline">
              Export Transactions
            </Button>
            {showExportMenu && (
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <button
                  onClick={handleExportTransactionsExcel}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg"
                >
                  Export to Excel
                </button>
                <button
                  onClick={handleExportTransactionsCSV}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg"
                >
                  Export to CSV
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Transaction History ({transactions.length})
            </h2>
          </div>

          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No transactions yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance After</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((txn) => (
                    <tr key={txn._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(txn.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                          txn.type === 'debt'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {txn.type === 'debt' ? 'Debt' : 'Payment'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${
                          txn.type === 'debt' ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {txn.type === 'debt' ? '+' : '-'}{formatCurrency(txn.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {txn.paymentMethod !== 'n/a' ? txn.paymentMethod.replace('_', ' ').toUpperCase() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(txn.balanceAfter)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {txn.notes || '-'}
                        {txn.isEdited && (
                          <span className="ml-2 text-xs text-yellow-600">(Edited)</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Last updated: {formatDateTime(debtor.lastUpdatedAt)}
        </div>
      </div>

      {/* Modals */}
      <ThresholdAlertModal
        isOpen={showThresholdAlert}
        onClose={() => setShowThresholdAlert(false)}
        alert={alert}
      />

      <AddDebtModal
        isOpen={showAddDebt}
        onClose={() => setShowAddDebt(false)}
        debtor={debtor}
        onSuccess={loadDebtorDetails}
      />

      <RecordPaymentModal
        isOpen={showRecordPayment}
        onClose={() => setShowRecordPayment(false)}
        debtor={debtor}
        onSuccess={loadDebtorDetails}
      />

      <EditDebtorModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        debtor={debtor}
        onSuccess={loadDebtorDetails}
      />

      <DeleteDebtorModal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        debtor={debtor}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default DebtorDetailPage;
